/**
 * InsufficientSparksContext
 *
 * Workspace-level event bus that lets node-level Generate buttons
 * trigger the global <InsufficientSparksModal>. Kept separate from
 * CanvasContext (which is large and React-Flow-coupled) so consumers
 * subscribe only to the modal-trigger event without re-rendering on
 * unrelated canvas state changes.
 *
 * Usage:
 *   - WorkspacePageV2 wraps its tree in <InsufficientSparksProvider>
 *     and reads `request` to render the modal.
 *   - Generate-button handlers call useShowInsufficientSparks() and
 *     invoke the returned function from their executeNode catch block.
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react'

import { trackInsufficientTokensBlock, type BlockedAction } from '../analytics/events'

export interface InsufficientSparksDetail {
  balance?: number
  required?: number
  /** Optional contextual node id — for diagnostics only; modal copy
   *  doesn't reference it today. */
  nodeId?: string
  /** Node type of whatever was blocked, used to classify the funnel event
   *  (#3503). Optional: a caller that genuinely cannot say produces
   *  `blocked_action: 'other'` rather than a guess. */
  nodeType?: string
  /** Owning project, for the funnel event. Every call site has it in scope. */
  projectId?: string
}

/**
 * Node type -> the funnel's `blocked_action` bucket.
 *
 * The spec names only video/image/audio. Our canvas also blocks on script and
 * storyboard generation, and forcing those into an image or video bucket would
 * quietly corrupt the split, so the enum is widened rather than approximated.
 * Anything unrecognised is 'other' — never a guess.
 */
export function classifyBlockedAction(nodeType: string | undefined): BlockedAction {
  switch (nodeType) {
    case 'video_generator':
      return 'video_generate'
    // Both spellings reach this function: the console types some node kinds in
    // camelCase (useFeedbackMode), the backend in snake_case.
    case 'image_generator':
    case 'character_designer':
    case 'location_designer':
    case 'object_designer':
    case 'characterDesigner':
    case 'locationDesigner':
    case 'objectDesigner':
      return 'image_generate'
    case 'sound_expert':
      return 'audio_generate'
    case 'script_writer':
      return 'text_generate'
    case 'storyboard_designer':
    case 'storyboard_no_enhance_designer':
      return 'storyboard_generate'
    default:
      return 'other'
  }
}

interface ContextValue {
  request: InsufficientSparksDetail | null
  show: (detail: InsufficientSparksDetail) => void
  dismiss: () => void
}

const Ctx = createContext<ContextValue>({
  request: null,
  show: () => {},
  dismiss: () => {},
})

/** Workspace-side hook — read the current request to drive the modal. */
export function useInsufficientSparks(): ContextValue {
  return useContext(Ctx)
}

/**
 * Component-side hook — get the stable `show` setter without
 * subscribing to `request`. Generate buttons should use this so they
 * don't re-render every time the modal opens/closes.
 */
export function useShowInsufficientSparks(): (detail: InsufficientSparksDetail) => void {
  return useContext(Ctx).show
}

export function InsufficientSparksProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [request, setRequest] = useState<InsufficientSparksDetail | null>(null)

  const show = useCallback((detail: InsufficientSparksDetail) => {
    // Emitted here rather than at the eleven call sites: this is the single
    // choke point every blocked generation already funnels through, so the
    // event cannot be missed when a new node type starts gating on credits.
    // It is the top of the purchase funnel — running out of credits is what
    // sends a user to the catalog in the first place.
    trackInsufficientTokensBlock({
      blocked_action: classifyBlockedAction(detail.nodeType),
      ...(detail.projectId === undefined ? {} : { project_id: detail.projectId }),
    })
    setRequest(detail)
  }, [])
  const dismiss = useCallback(() => setRequest(null), [])

  const value = useMemo<ContextValue>(
    () => ({ request, show, dismiss }),
    [request, show, dismiss],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
